import { useCallback, useEffect, useMemo, useState } from "react";
import { createPrintJob } from "../api/printJobs";
import { generateBarcodeDataUrl } from "../utils/barcodes";
import Table from "./Table";

const DEFAULT_COLUMNS = [
  { key: "name", label: "Name", width: "minmax(200px, 2fr)" },
  { key: "refCode", label: "Ref Code", width: "minmax(120px, 1fr)" },
  { key: "quantity", label: "Quantity", align: "right", width: "minmax(90px, 1fr)" },
  { key: "price", label: "Price", align: "right", width: "minmax(110px, 1fr)" },
  { key: "status", label: "Status", width: "minmax(120px, 1fr)" },
];

const normalizeId = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string" || typeof value === "number") return `${value}`;
  if (value?.$oid) return value.$oid;
  return value.toString?.() || "";
};

const useResettableState = (value, deps = []) => {
  const [state, setState] = useState(value);
  useEffect(() => {
    setState(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return [state, setState];
};

function BarcodeModal({
  open,
  rows = [],
  columns = [],
  defaultSelection = [],
  loading = false,
  onClose,
}) {
  const [search, setSearch] = useResettableState("", [open]);
  const [selection, setSelection] = useResettableState(new Set(), [open]);
  const [selectionRows, setSelectionRows] = useResettableState([], [open]);
  const [renderedLabels, setRenderedLabels] = useResettableState([], [open]);
  const [printError, setPrintError] = useResettableState("", [open]);
  const [printing, setPrinting] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [sendCounts, setSendCounts] = useState({});
  const [resetSelectionKey, setResetSelectionKey] = useState(0);

  const selectionSeed = useMemo(() => {
    if (!defaultSelection) return [];
    if (defaultSelection instanceof Set) return Array.from(defaultSelection).map(normalizeId).filter(Boolean);
    if (Array.isArray(defaultSelection)) return defaultSelection.map(normalizeId).filter(Boolean);
    return [];
  }, [defaultSelection]);

  useEffect(() => {
    if (!open) return;
    setResetSelectionKey((key) => key + 1);
    setSendConfirmOpen(false);
    setSendCounts({});
    setSending(false);
  }, [open]);

  const matchesQuery = useCallback(
    (row) => {
      const value = search.trim().toLowerCase();
      if (!value) return true;
      const product = row?.product ?? row ?? {};
      const haystack = [
        row?.name,
        row?.refCode,
        row?.barcode,
        product?.name,
        product?.refCode,
        product?.sku,
        product?.barcode,
      ]
        .filter(Boolean)
        .map((entry) => String(entry).toLowerCase());
      return haystack.some((entry) => entry.includes(value));
    },
    [search]
  );

  const filteredRows = useMemo(() => (Array.isArray(rows) ? rows.filter(matchesQuery) : []), [rows, matchesQuery]);
  const tableColumns = useMemo(() => (columns?.length ? columns : DEFAULT_COLUMNS), [columns]);

  const handleSelectionChange = useCallback((payload = {}) => {
    const ids = payload.ids instanceof Set ? payload.ids : new Set(payload.ids || []);
    const payloadRows = Array.isArray(payload.rows) ? payload.rows : [];
    setSelection(ids);
    setSelectionRows(payloadRows);
    setPrintError("");
  }, [setSelection, setSelectionRows, setPrintError]);

  const getProductFromRow = useCallback((row) => {
    if (!row) return {};
    const product = row.product ?? row;
    const id = normalizeId(product?.id || product?._id || row.id || row._id || row.refCode || row.sku || row.barcode);
    const name = product?.name || row?.name || product?.title || product?.description || row?.refCode || "Untitled product";
    const refCode = product?.refCode || row?.refCode || product?.sku || "";
    const barcode = product?.barcode || refCode || id;
    const quantityRaw = product?.quantityOnHand ?? product?.quantity ?? row?.quantity;
    const quantityNumeric = Number(quantityRaw);
    const quantity = Number.isFinite(quantityNumeric) ? quantityNumeric : null;
    return { id, name, refCode, barcode, quantity };
  }, []);

  const buildDefaultSendCounts = useCallback(() => {
    const counts = {};
    selectionRows.forEach((row) => {
      const product = getProductFromRow(row);
      const key = product.id || product.barcode || product.refCode || product.name;
      if (!key) return;
      const base = Number.isFinite(product.quantity) && product.quantity > 0 ? Math.floor(product.quantity) : 1;
      counts[key] = base || 1;
    });
    return counts;
  }, [getProductFromRow, selectionRows]);

  const handlePrint = useCallback(async () => {
    if (!selectionRows.length) {
      setPrintError("Select at least one product to print.");
      return;
    }
    setPrinting(true);
    setPrintError("");
    try {
      const labels = await Promise.all(
        selectionRows.map(async (row) => {
          const product = getProductFromRow(row);
          if (!product.barcode) {
            throw new Error("One or more selected products are missing barcodes.");
          }
          const image = await generateBarcodeDataUrl(product.barcode);
          return { ...product, image };
        })
      );
      setRenderedLabels(labels);
    } catch (err) {
      setPrintError(err?.message || "Unable to generate barcode labels.");
    } finally {
      setPrinting(false);
    }
  }, [getProductFromRow, selectionRows, setRenderedLabels, setPrintError]);

  const handleStartSendToPrinter = useCallback(() => {
    if (!selectionRows.length) {
      setPrintError("Select at least one product to send to printer.");
      return;
    }
    setSendCounts(buildDefaultSendCounts());
    setSendConfirmOpen(true);
  }, [buildDefaultSendCounts, selectionRows, setPrintError]);

  const handleChangeSendCount = (key) => (event) => {
    const value = Math.max(1, Math.floor(Number(event?.target?.value) || 1));
    setSendCounts((prev) => ({ ...prev, [key]: value }));
  };

  const handleConfirmSend = useCallback(async () => {
    if (!selectionRows.length) {
      setPrintError("Select at least one product to send to printer.");
      setSendConfirmOpen(false);
      return;
    }
    setSending(true);
    setPrintError("");
    try {
      const labels = [];
      for (const row of selectionRows) {
        const product = getProductFromRow(row);
        const key = product.id || product.barcode || product.refCode || product.name;
        if (!key) continue;
        if (!product.barcode) {
          throw new Error("One or more selected products are missing barcodes.");
        }
        const count = Math.max(1, Math.floor(Number(sendCounts[key]) || 1));
        for (let i = 0; i < count; i += 1) {
          labels.push({ ...product, instanceId: `${key}-${i + 1}` });
        }
      }
      if (!labels.length) {
        throw new Error("No labels available to print.");
      }

      const response = await createPrintJob({ labels });
      const jobId = response?.jobId;
      const token = response?.token;

      if (!jobId || !token) {
        throw new Error("Unable to create a print job.");
      }

      const printUrl = new URL(window.location.origin);
      printUrl.pathname = window.location.pathname;
      printUrl.searchParams.set("print", "barcodes");
      printUrl.searchParams.set("jobId", jobId);
      printUrl.searchParams.set("token", token);

      const printWindow = window.open(printUrl.toString(), "_blank", "noopener,noreferrer");

      if (!printWindow) {
        throw new Error("Popup blocked. Please allow popups to print labels.");
      }

      setSendConfirmOpen(false);
    } catch (err) {
      setPrintError(err?.message || "Unable to prepare labels for printer.");
    } finally {
      setSending(false);
    }
  }, [getProductFromRow, selectionRows, sendCounts, setPrintError, setSendConfirmOpen, setSending]);

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose?.();
    }
  };

  if (!open) return null;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Print barcodes" onClick={handleOverlayClick}>
      <div className="barcode-modal" onClick={(event) => event.stopPropagation()}>
        <header className="barcode-modal__header">
          <div>
            <p className="muted extra-small">Barcode labels</p>
            <h2>Print labels</h2>
          </div>
          <button className="ghost-btn subtle" type="button" onClick={onClose} aria-label="Close barcode modal">
            ✕
          </button>
        </header>

        <div className="barcode-modal__body">
          <div className="barcode-modal__toolbar">
            <div className="table-search compact">
              <div className="table-search-inner">
                <span className="search-icon" aria-hidden="true">
                  🔍
                </span>
                <input
                  type="search"
                  placeholder="Search by name, ref code, or barcode"
                  aria-label="Search products"
                  value={search}
                  onChange={(event) => setSearch(event?.target?.value || "")}
                  disabled={loading}
                />
              </div>
            </div>
            <div className="barcode-modal__meta">
              <span className="pill muted">{selection.size} selected</span>
            </div>
          </div>

          {printError ? <div className="inline-alert error compact">{printError}</div> : null}

          <div className="barcode-modal__table">
            <Table
              columns={tableColumns}
              rows={filteredRows}
              resetSelectionKey={`${resetSelectionKey}`}
              onSelectionChange={handleSelectionChange}
              enableSelection
              defaultSelectedIds={selectionSeed}
              stackOnMobile
            />
          </div>

          <footer className="barcode-modal__footer">
            <div className="muted small">Labels render at ~2:1 ratio.</div>
            <div className="barcode-modal__actions">
              <button
                className="ghost-btn"
                type="button"
                onClick={handleStartSendToPrinter}
                disabled={sending || !selection.size}
              >
                Send to printer
              </button>
              <button className="primary-btn" type="button" onClick={handlePrint} disabled={printing || !selection.size}>
                {printing ? "Generating..." : "View Barcodes"}
              </button>
            </div>
          </footer>

          {renderedLabels.length ? (
            <div className="barcode-preview">
              <div className="barcode-preview__head">
                <div>
                  <p className="muted extra-small">Generated labels</p>
                  <h4>Generated labels</h4>
                </div>
                <span className="pill muted">{renderedLabels.length} item{renderedLabels.length === 1 ? "" : "s"}</span>
              </div>
              <div className="barcode-preview__grid">
                {renderedLabels.map((label) => (
                  <div className="barcode-label" key={label.id || label.barcode || label.refCode}>
                    <div className="barcode-label__meta">
                      <div className="barcode-label__name">{label.name}</div>
                    </div>
                    <div className="barcode-label__image">
                      {label.image ? <img src={label.image} alt={`Barcode for ${label.name}`} /> : null}
                    </div>
                    <div className="barcode-label__code">{label.barcode}</div>
                    <div className="barcode-label__footer" />
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
        {sendConfirmOpen ? (
          <div className="barcode-send-confirm" role="dialog" aria-modal="true" aria-label="Confirm print quantities">
            <div className="barcode-send-card">
              <div className="barcode-send-head">
                <div>
                  <p className="muted extra-small">Confirm quantities</p>
                  <h4>Send to printer</h4>
                  <p className="muted small">Defaults are pulled from current stock. Adjust if needed.</p>
                </div>
                <button className="ghost-btn subtle" type="button" onClick={() => setSendConfirmOpen(false)} aria-label="Close quantity confirmation">
                  ✕
                </button>
              </div>
              <div className="barcode-send-list">
                {selectionRows.map((row) => {
                  const product = getProductFromRow(row);
                  const key = product.id || product.barcode || product.refCode || product.name;
                  const value = sendCounts[key] ?? 1;
                  const stockLabel =
                    Number.isFinite(product.quantity) && product.quantity >= 0
                      ? `Stock: ${product.quantity}`
                      : "Stock: —";
                  return (
                    <div className="barcode-send-row" key={key}>
                      <div className="barcode-send-info">
                        <div className="barcode-send-name">{product.name}</div>
                        <div className="barcode-send-meta">
                          <span className="muted extra-small">{product.barcode || product.refCode || "No barcode"}</span>
                          <span className="muted extra-small">{stockLabel}</span>
                        </div>
                      </div>
                      <input
                        type="number"
                        min="1"
                        className="barcode-send-input"
                        value={value}
                        onChange={handleChangeSendCount(key)}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="barcode-send-actions">
                <button className="ghost-btn" type="button" onClick={() => setSendConfirmOpen(false)} disabled={sending}>
                  Cancel
                </button>
                <button className="primary-btn" type="button" onClick={handleConfirmSend} disabled={sending}>
                  {sending ? "Preparing..." : "OK"}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default BarcodeModal;
